import React, { useEffect, useRef, useState, Suspense } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import Navbar from "../components/navbar";
import Sidebar from "../components/sidebar";
import { AppBackground } from "../components/ui";
import { useSubscription } from "../routers/ProtectedRoute";

const ExpiryBanner = () => {
  const { daysLeft } = useSubscription();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("expiry_banner_dismissed") === "1");

  if (dismissed || daysLeft === null || daysLeft > 7) return null;

  const label = daysLeft === 0 ? "expires today" : `expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}`;

  return (
    <div className="flex items-center justify-between gap-3 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-900/60 px-4 py-2 text-sm text-amber-800 dark:text-amber-300">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
        <span>Your subscription <strong>{label}</strong>. Renew now to avoid interruption.</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <Link to="/billing" className="font-semibold underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200">Renew</Link>
        <button
          onClick={() => { sessionStorage.setItem("expiry_banner_dismissed", "1"); setDismissed(true); }}
          className="text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200 text-lg leading-none"
        >&times;</button>
      </div>
    </div>
  );
};

const PageFallback = () => (
  <div className="flex items-center justify-center py-20">
    <Loader2 className="h-7 w-7 animate-spin text-brand-600 dark:text-brand-400" />
  </div>
);

const AnimatedOutlet = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <Suspense fallback={<PageFallback />}>
          <Outlet />
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

const AppLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return;

    const lenis = new Lenis({
      wrapper: wrapperRef.current,
      content: contentRef.current,
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);

    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50/70 dark:bg-[#0b0f17] text-slate-900 dark:text-ink-100 font-sans transition-colors duration-200">
      <Sidebar

        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
      />
      <div className="flex min-w-0 flex-1 flex-col h-screen overflow-hidden transition-all duration-300 relative">
        <AppBackground />
        <Navbar toggleSidebar={() => setIsSidebarOpen((v) => !v)} collapsed={collapsed} />
        <ExpiryBanner />
        <main ref={wrapperRef} className="flex-1 overflow-y-auto relative z-10">
          <div ref={contentRef} className="p-4 lg:p-6">
            <AnimatedOutlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
